import { useParams } from "react-router-dom";
import Seo from "@/components/common/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ManhuaCard from "@/components/feature/ManhuaCard";
import NotFound from "@/pages/NotFound";
import { manhuaList } from "@/mocks/manhua";

export default function DetailPage() {
  const { id } = useParams();
  const item = manhuaList.find((m) => m.id === id);

  if (!item) return <NotFound />;

  const related = manhuaList.filter((m) => m.category === item.category && m.id !== item.id).slice(0, 4);
  const chapters = Array.from({ length: Math.min(item.chapters, 24) }, (_, i) => item.chapters - i);

  return (
    <>
      <Seo
        title={`${item.title} 好看的韩漫大全在线阅读`}
        description={`${item.title}是一部${item.category}题材韩漫，由${item.author}创作，韩漫大全在线阅读站收录${item.chapters}话内容，支持免费在线阅读与追更。`}
        keywords={`${item.title},韩漫,${item.category},韩漫在线阅读`}
        canonical={`https://hkmans1.github.io/detail/${item.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: item.title,
          author: { "@type": "Person", name: item.author },
          genre: item.category,
          description: item.intro,
          image: item.cover,
          numberOfPages: item.chapters,
        }}
      />

      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:py-12">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "全部韩漫", to: "/list" },
            { label: item.title },
          ]}
        />

        <section className="mt-5 flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="w-full shrink-0 md:w-56 lg:w-64">
            <div className="aspect-[7/10] w-full overflow-hidden rounded-xl bg-background-200">
              <img src={item.cover} alt={`${item.title} 韩漫封面`} className="h-full w-full object-cover object-top" />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">{item.status}</span>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">{item.category}</span>
            </div>
            <h1 className="heading-font mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">{item.title}</h1>
            <p className="mt-2 text-sm text-foreground-600">作者：{item.author} · 更新至 {item.latestChapter}</p>

            <div className="mt-4 flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-sm text-foreground-700">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-md">
              <div className="rounded-xl bg-background-100 p-4 text-center">
                <p className="text-xl font-bold text-accent-600">{item.rating}</p>
                <p className="mt-1 text-xs text-foreground-600">评分</p>
              </div>
              <div className="rounded-xl bg-background-100 p-4 text-center">
                <p className="text-xl font-bold text-foreground-950">{item.views}</p>
                <p className="mt-1 text-xs text-foreground-600">人气</p>
              </div>
              <div className="rounded-xl bg-background-100 p-4 text-center">
                <p className="text-xl font-bold text-foreground-950">{item.chapters}</p>
                <p className="mt-1 text-xs text-foreground-600">话数</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href="#chapters"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                <i className="ri-book-open-line" /> 立即在线阅读
              </a>
              <a
                href="#related"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background-100 px-6 py-3 text-sm font-bold text-foreground-800 whitespace-nowrap transition-colors hover:bg-background-200"
              >
                <i className="ri-lightbulb-flash-line" /> 看看同类韩漫
              </a>
            </div>
          </div>
        </section>

        <section className="mt-10 rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8">
          <h2 className="heading-font text-xl font-bold text-foreground-950">剧情简介</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">{item.intro}</p>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            《{item.title}》是近年来口碑与热度俱佳的一部{item.category}题材韩漫，画风细腻、剧情节奏紧凑。如果你是好看的韩漫大全的忠实读者，这部作品值得加入你的收藏站点，韩漫大全在线阅读站将持续为你跟进最新章节更新。
          </p>
        </section>

        <section id="chapters" className="mt-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="heading-font text-xl font-bold text-foreground-950">章节列表</h2>
            <p className="text-sm text-foreground-600">最近 {chapters.length} 话 · 点击标题跳转书源阅读</p>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((n) => (
              <a
                key={n}
                href={`https://jisu-hanman.example.com/chapter/${item.id}/${n}`}
                target="_blank"
                rel="nofollow noopener"
                className="flex items-center justify-between rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
              >
                <span className="whitespace-nowrap">{item.title} 第 {n} 话</span>
                <i className="ri-play-circle-line text-lg" />
              </a>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section id="related" className="mt-12">
            <h2 className="heading-font text-xl font-bold text-foreground-950">同类推荐</h2>
            <p className="mt-2 text-sm text-foreground-600">喜欢《{item.title}》的读者可能还会喜欢</p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {related.map((m) => (
                <ManhuaCard key={m.id} item={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}